"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.createApiClient = createApiClient;
const axios_1 = __importStar(require("axios"));
const errors_1 = require("./errors");
/** Reshapes the backend's `{ success, data, meta }` envelope into `T` or `{ items, ...meta }`. */
function unwrapEnvelope(raw) {
    const envelope = raw;
    if (envelope && typeof envelope === "object" && envelope.success === true && "data" in envelope) {
        return envelope.meta ? { items: envelope.data, ...envelope.meta } : envelope.data;
    }
    return raw;
}
const DEFAULT_SKIP_REFRESH_PATHS = [
    "/auth/refresh",
    "/auth/login",
    "/auth/logout",
    "/auth/register",
    "/auth/verify-code",
    "/auth/send-code",
    "/auth/resend-code",
    "/admin/auth/login",
    "/admin/auth/logout",
    "/admin/auth/me",
    "/users/me",
    "/auth/me",
];
function createApiClient(options) {
    const shouldUnwrap = options.unwrapEnvelope ?? true;
    const client = axios_1.default.create({
        baseURL: options.baseURL,
        timeout: options.timeout ?? 15000,
        headers: { "Content-Type": "application/json" },
        withCredentials: options.mode === "cookie",
    });
    if (options.mode === "bearer") {
        const { tokenKey, tokenStorage, onUnauthorized } = options;
        client.interceptors.request.use(async (config) => {
            const token = await tokenStorage.getItem(tokenKey);
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });
        client.interceptors.response.use((response) => {
            if (shouldUnwrap)
                response.data = unwrapEnvelope(response.data);
            return response;
        }, async (error) => {
            if (error.response?.status === 401) {
                await tokenStorage.removeItem(tokenKey);
                onUnauthorized?.();
            }
            return Promise.reject(error);
        });
        return client;
    }
    // ─── Cookie mode: refresh-on-401 with request-queue dedup ──────────────────
    const { refreshPath = "/auth/refresh", onSessionExpired } = options;
    const skipPaths = options.skipRefreshPaths ?? DEFAULT_SKIP_REFRESH_PATHS;
    const shouldSkipRefresh = (url = "") => skipPaths.some((path) => url.includes(path));
    let isRefreshing = false;
    let refreshQueue = [];
    const processRefreshQueue = (error) => {
        refreshQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve()));
        refreshQueue = [];
    };
    const refreshSession = () => axios_1.default.post(`${options.baseURL}${refreshPath}`, {}, { withCredentials: true });
    client.interceptors.response.use((response) => (shouldUnwrap ? unwrapEnvelope(response.data) : response.data), async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 &&
            originalRequest &&
            !originalRequest._retry &&
            !shouldSkipRefresh(originalRequest.url || "")) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => refreshQueue.push({ resolve, reject }))
                    .then(() => client(originalRequest))
                    .catch((queueError) => Promise.reject(queueError));
            }
            originalRequest._retry = true;
            isRefreshing = true;
            try {
                await refreshSession();
                processRefreshQueue(null);
                return client(originalRequest);
            }
            catch (refreshError) {
                processRefreshQueue(refreshError);
                onSessionExpired?.();
                const hasResponse = (0, axios_1.isAxiosError)(refreshError) && refreshError.response;
                return Promise.reject((0, errors_1.normalizeResponseError)(hasResponse ? refreshError : error));
            }
            finally {
                isRefreshing = false;
            }
        }
        return Promise.reject((0, errors_1.normalizeResponseError)(error));
    });
    return client;
}
